import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, Hint, Icon, Label, Skeleton, Switch, cn, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useAgentsList, useAudienceLabel, useAudiences } from "../shared/use-shared-queries";
import type { SourceUsage, SourceView } from "../shared/types";
import { useAccessMatrix, useInvalidateSource } from "./use-source-detail";

interface AccessCardProps {
  source: SourceView;
  usage: SourceUsage | undefined;
  canEdit: boolean;
}

function AgentMapping({ usage }: { usage: SourceUsage | undefined }) {
  const { t } = useTranslation();
  const label = useAudienceLabel();
  const agents = useAgentsList();
  const linked = new Set((usage?.agents ?? []).map((a) => a.id));
  const rows = (agents.data ?? []).filter((agent) => linked.has(agent.id));
  if (rows.length === 0) return null;
  return (
    <ul className="space-y-1 rounded-md bg-surface-2 px-3 py-2.5 text-xs text-fg-muted">
      {rows.map((agent) => (
        <li key={agent.id}>{t("deployKnowledge.sourceDetail.access.agentMapping", { agent: agent.name, audiences: agent.audiences.map(label).join(", ") })}</li>
      ))}
    </ul>
  );
}

/** Inherit switch + audience chips (tenant labels) → `PATCH /v1/sources/{id}`. */
export function AccessCard({ source, usage, canEdit }: AccessCardProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateSource(source.id);
  const audiences = useAudiences();
  const matrix = useAccessMatrix();
  const switchId = useId();
  const k = "deployKnowledge.sourceDetail.access";
  const serverInherit = matrix.data?.sources.find((s) => s.source_id === source.id)?.inherit_audiences;
  const [inherit, setInherit] = useState<boolean | null>(null);
  const [codes, setCodes] = useState<string[]>(source.audiences.map(String));
  const effectiveInherit = inherit ?? serverInherit ?? false;
  const inheritKnown = serverInherit !== undefined;
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH("/v1/sources/{source_id}", {
          params: { path: { source_id: source.id } },
          body: { audiences: codes.map(Number), clear_valid_until: false, ...(inheritKnown ? { inherit_audiences: effectiveInherit } : {}) },
        }),
      ),
    onSuccess: async () => {
      toast.success(t(effectiveInherit ? `${k}.inheritOn` : `${k}.saved`));
      setInherit(null);
      await invalidate();
    },
    onError: (error) => toast.error(message(error)),
  });
  const options = (audiences.data ?? []).map((a) => ({ value: String(a.code), label: a.label, disabled: !canEdit || effectiveInherit }));
  const empty = codes.length === 0;
  return (
    <section className="card" aria-labelledby="source-access-title">
      <div className="card-header">
        <h2 id="source-access-title" className="card-title flex items-center gap-2">
          <Icon name="shield-check" className="size-4 text-primary-ink" />
          {t(`${k}.title`)}
        </h2>
      </div>
      <div className="card-body space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label htmlFor={switchId} className="mb-0 text-sm font-semibold">
              {t(`${k}.inherit`)}
            </Label>
            <Hint className="mt-0.5">{inheritKnown ? t(`${k}.inheritHint`) : t(`${k}.inheritUnknown`)}</Hint>
          </div>
          <Switch id={switchId} className="mt-0.5" checked={effectiveInherit} disabled={!canEdit || !inheritKnown} onCheckedChange={setInherit} />
        </div>
        <div className={cn(effectiveInherit && "opacity-50")}>
          <p className="label">
            {t(`${k}.who`)}
          </p>
          {audiences.isPending ? (
            <Skeleton className="h-8 w-full" />
          ) : (
            <ChoiceChips multiple variant="pill" aria-label={t(`${k}.who`)} options={options} value={codes} onValueChange={setCodes} />
          )}
          <Hint className="mt-1.5">{empty ? t(`${k}.atLeastOne`) : t(`${k}.whoHint`)}</Hint>
        </div>
        {effectiveInherit ? <AgentMapping usage={usage} /> : null}
        {canEdit ? (
          <Button variant="secondary" size="sm" className="w-full" disabled={empty} loading={save.isPending} onClick={() => { save.mutate(); }}>
            {t(`${k}.save`)}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
